import { JobV2 } from "../@types/job";
import { Client } from "../@types/client";
import { User } from "../@types/user";
import { PaymentType } from "../@types/enums";
import { stripProcessingFeeServiceNameFromLineItems } from "./processingFees";
import { authenticatedFetch } from "../_utils/authenticatedFetch";
import { QBBankAccount, QBTaxGroup } from "../@types/quickbooks";
import { isQbToBillInvoiceJob } from "../_constants/tags";
import { toDecimal } from "./math";
import Decimal from "decimal.js";

export type QuickBooksSyncErrorCode = "QB_CATEGORY_ITEM" | "QB_SYNC_FAILED";

export function formatQuickBooksCategoryConflictMessage(
  itemNames: string[]
): string {
  if (itemNames.length === 0) {
    return "Could not save — one or more line items match QuickBooks Categories.";
  }

  if (itemNames.length === 1) {
    return `Could not save — "${itemNames[0]}" matches a QuickBooks Category. Rename the line item in MountMonitor or the Category in QuickBooks.`;
  }

  const quotedNames = itemNames.map((name) => `"${name}"`).join(", ");
  return `Could not save — ${itemNames.length} line items match QuickBooks Categories: ${quotedNames}. Rename them in MountMonitor or rename the Categories in QuickBooks.`;
}

export class QuickBooksSyncError extends Error {
  readonly code: QuickBooksSyncErrorCode;
  readonly itemNames: string[];

  constructor(options: {
    message: string;
    code: QuickBooksSyncErrorCode;
    itemNames?: string[];
  }) {
    super(options.message);
    this.name = "QuickBooksSyncError";
    this.code = options.code;
    this.itemNames = options.itemNames ?? [];
  }
}

export function isQuickBooksSyncError(
  error: unknown
): error is QuickBooksSyncError {
  return error instanceof QuickBooksSyncError;
}

export function throwIfQuickBooksApiError(payload: {
  error?: string;
  code?: string;
  itemName?: string;
  itemNames?: string[];
}) {
  if (!payload.error) {
    return;
  }

  const itemNames =
    payload.itemNames ?? (payload.itemName ? [payload.itemName] : []);

  if (payload.code === "QB_CATEGORY_ITEM" && itemNames.length > 0) {
    throw new QuickBooksSyncError({
      message: formatQuickBooksCategoryConflictMessage(itemNames),
      code: "QB_CATEGORY_ITEM",
      itemNames,
    });
  }

  throw new QuickBooksSyncError({
    message: payload.error,
    code: "QB_SYNC_FAILED",
  });
}

// Only recover recent jobs. Older synced jobs with no invoice id include
// imports that may already have a QB invoice, and a new one would double-bill.
const MISSING_INVOICE_RECOVERY_DAYS = 30;

// A job whose invoice create failed stays flagged as synced with no invoice id,
// and every later sync fails updating an invoice that doesn't exist.
function isRecoverableMissingInvoice(job: JobV2): boolean {
  if (!job.syncedToAccountingIntegration || job.qbInvoiceId) {
    return false;
  }

  const createdMs = job.createdAt?.toMillis?.();
  return (
    !!createdMs &&
    Date.now() - createdMs < MISSING_INVOICE_RECOVERY_DAYS * 24 * 60 * 60 * 1000
  );
}

export function jobUpdateNeedsQuickBooksInvoiceSync(
  currentJob: JobV2,
  updatedJob: JobV2
): boolean {
  if (!updatedJob.syncedToAccountingIntegration) {
    return false;
  }

  if (
    !currentJob.syncedToAccountingIntegration ||
    isRecoverableMissingInvoice(currentJob)
  ) {
    return true;
  }

  // The invoice's CustomerRef is re-sent on every full update, so a client
  // switch only needs a sync to fire to move the invoice to the new customer.
  const hasClientChange = currentJob.client?.uid !== updatedJob.client?.uid;

  const isNewDepositOrFullPayment =
    updatedJob.payments.length === 1 &&
    (updatedJob.payments[0].type === PaymentType.Deposit ||
      updatedJob.payments[0].type === PaymentType.Full) &&
    (currentJob.payments.length === 0 ||
      currentJob.payments[0].type !== updatedJob.payments[0].type ||
      currentJob.payments[0].amount !== updatedJob.payments[0].amount);

  const hasLineItemChanges =
    currentJob.lineItems.length !== updatedJob.lineItems.length;

  const hasTaxChange = currentJob.includeTax !== updatedJob.includeTax;

  const priceChanges = updatedJob.lineItems.some(
    (lineItem) =>
      (lineItem.price || 0) !==
      (currentJob.lineItems.find((item) => item.uid === lineItem.uid)?.price ||
        0)
  );

  const discountChanges = updatedJob.lineItems.some(
    (lineItem) =>
      (lineItem.discount || 0) !==
      (currentJob.lineItems.find((item) => item.uid === lineItem.uid)
        ?.discount || 0)
  );

  // Compared in whole cents. The job form recomputes percentage deposits
  // rounded to cents on open, so an old job storing 1108.6975 comes back as
  // 1108.70 and would otherwise trigger a full invoice update on every save
  const toCents = (value: number | string | null | undefined) =>
    toDecimal(Number(value) || 0)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber();

  const depositChanges = updatedJob.lineItems.some(
    (lineItem) =>
      toCents(lineItem.deposit) !==
      toCents(
        currentJob.lineItems.find((item) => item.uid === lineItem.uid)?.deposit
      )
  );

  return (
    hasClientChange ||
    hasLineItemChanges ||
    isNewDepositOrFullPayment ||
    hasTaxChange ||
    priceChanges ||
    discountChanges ||
    depositChanges
  );
}

async function checkQuickBooksLineItems(
  lineItems: JobV2["lineItems"],
  incomeAccountId: string
) {
  const strippedLineItems = stripProcessingFeeServiceNameFromLineItems(lineItems);
  const response = await authenticatedFetch(`/api/quickbooks/items/checkItems`, {
    method: "POST",
    body: JSON.stringify({
      lineItems: strippedLineItems,
      incomeAccountId,
    }),
  });
  const payload = await response.json();
  throwIfQuickBooksApiError(payload);
  return payload.data as Record<string, string>;
}

export async function validateQuickBooksLineItemsForJob(
  user: User,
  lineItems: JobV2["lineItems"]
) {
  const incomeAccountId = user.settings.qbIncomeAccount?.id;
  if (!incomeAccountId) {
    return;
  }

  await checkQuickBooksLineItems(lineItems, incomeAccountId);
}

export async function preflightQuickBooksJobUpdateSync({
  user,
  currentJob,
  updatedJob,
  client,
}: {
  user: User;
  currentJob: JobV2;
  updatedJob: JobV2;
  client: Client;
}): Promise<string | undefined> {
  const { qbIncomeAccount, qbBankAccount, qbTaxGroup } = user.settings;

  // QB-billed jobs adopt an existing QB invoice — never create/update one
  if (isQbToBillInvoiceJob(updatedJob.tags)) {
    return undefined;
  }

  if (
    !updatedJob.syncedToAccountingIntegration ||
    !qbIncomeAccount ||
    !qbBankAccount ||
    !qbTaxGroup
  ) {
    return undefined;
  }

  if (!jobUpdateNeedsQuickBooksInvoiceSync(currentJob, updatedJob)) {
    return undefined;
  }

  // The invoice routes read client.quickbooks.id; a client that was never
  // synced has no such field and the call crashes with a TypeError.
  if (!client.quickbooks?.id) {
    throw new Error(
      `${client.fullName} is not synced to QuickBooks, so this job's invoice can't be saved. Turn QuickBooks sync on for the client first, or turn it off for this job.`
    );
  }

  const qbItems = await checkQuickBooksLineItems(
    updatedJob.lineItems,
    qbIncomeAccount.id
  );

  const jobPayload = {
    ...updatedJob,
    qbInvoiceId: currentJob.qbInvoiceId || updatedJob.qbInvoiceId,
  };

  const recoverMissingInvoice = isRecoverableMissingInvoice(currentJob);

  if (!currentJob.syncedToAccountingIntegration || recoverMissingInvoice) {
    const response = await authenticatedFetch(`/api/quickbooks/job/create`, {
      method: "POST",
      body: JSON.stringify({
        userCountry: user.settings.address.country,
        job: jobPayload,
        client,
        qbItems,
        qbTaxGroup,
        qbBankAccount,
        recoverMissingInvoice,
      }),
    });
    const payload = await response.json();
    throwIfQuickBooksApiError(payload);
    return payload.data.Id as string;
  }

  const response = await authenticatedFetch(`/api/quickbooks/job/update`, {
    method: "POST",
    body: JSON.stringify({
      userCountry: user.settings.address.country,
      job: jobPayload,
      client,
      qbItems,
      qbTaxGroup: qbTaxGroup as QBTaxGroup,
      qbBankAccount: qbBankAccount as QBBankAccount,
    }),
  });
  const payload = await response.json();
  throwIfQuickBooksApiError(payload);
  return payload.data.Id as string;
}
